import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../api/baseQuery';

// Using centralized baseQueryWithReauth from api/baseQuery.js
export const surveyApi = createApi({
  reducerPath: 'surveyApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Survey', 'SurveyReport'],
  endpoints: (builder) => ({
    // GET /survey/list
    getSurveys: builder.query({
      query: (params = {}) => {
        // Clean and prepare query parameters
        const queryParams = {
          page: typeof params === 'number' ? params : (params.page || 0),
          pageSize: params.pageSize || 8,
          ...(params.keyword && params.keyword.trim() && { keyword: params.keyword.trim() }),
          ...(params.status !== undefined && { status: params.status }),
          ...(params.type !== undefined && { type: params.type }),
          ...(params.startDate && { startDate: params.startDate }),
          ...(params.endDate && { endDate: params.endDate }),
          // Only include sort parameters if they differ from server defaults
          ...(params.sortBy && { sortBy: params.sortBy }),
          ...(params.sortOrder && { sortOrder: params.sortOrder })
        };

        console.log('🔍 Survey API Query Params (keyword):', queryParams);

        return {
          url: '/survey/list',
          method: 'GET',
          params: queryParams
        };
      },
      transformResponse: (response) => ({
        surveys: response.value.surveys || [],
        totalCount: response.value.totalCount || 0,
        isSuccess: response.isSuccess,
        errors: response.errors || [],
        validationErrors: response.validationErrors || [],
      }),
      providesTags: (result) =>
        result?.surveys
          ? [
              ...result.surveys.map(({ id }) => ({ type: 'Survey', id })),
              { type: 'Survey', id: 'LIST' },
            ]
          : [{ type: 'Survey', id: 'LIST' }],
    }),

    // GET /survey/list-by-tenant - New endpoint for company users
    getSurveysByTenant: builder.query({
      query: (params = {}) => {
        // Clean and prepare query parameters
        const queryParams = {
          page: typeof params === 'number' ? params : (params.page || 0),
          pageSize: params.pageSize || 8,
          ...(params.keyword && params.keyword.trim() && { keyword: params.keyword.trim() }),
          ...(params.status !== undefined && { status: params.status }),
          ...(params.type !== undefined && { type: params.type }),
          ...(params.startDate && { startDate: params.startDate }),
          ...(params.endDate && { endDate: params.endDate }),
          // Only include sort parameters if they differ from server defaults
          ...(params.sortBy && { sortBy: params.sortBy }),
          ...(params.sortOrder && { sortOrder: params.sortOrder })
        };

        console.log('🔍 Survey Tenant API Query Params (keyword):', queryParams);

        return {
          url: '/survey/list',
          method: 'GET',
          params: queryParams
        };
      },
      transformResponse: (response) => ({
        surveys: response.value.surveys || [],
        totalCount: response.value.totalCount || 0,
        isSuccess: response.isSuccess,
        errors: response.errors || [],
        validationErrors: response.validationErrors || [],
      }),
      providesTags: (result) =>
        result?.surveys
          ? [
              ...result.surveys.map(({ id }) => ({ type: 'Survey', id })),
              { type: 'Survey', id: 'TENANT_LIST' },
            ]
          : [{ type: 'Survey', id: 'TENANT_LIST' }],
    }),

    // GET /survey/{surveyId}
    getSurveyById: builder.query({
      query: (id) => ({
        url: `/survey/${id}`,
        method: 'GET',
      }),
      transformResponse: (response) => response.value,
      providesTags: (result, error, id) => [{ type: 'Survey', id }],
    }),

    // GET /survey/{surveyId} with tenant context
    getTenantSurveyById: builder.query({
      query: (id) => ({
        url: `/survey/${id}`,
        method: 'GET',
      }),
      transformResponse: (response) => response.value,
      providesTags: (result, error, id) => [{ type: 'Survey', id }],
    }),

    // POST /survey/create
    createSurvey: builder.mutation({
      query: (data) => ({
        url: '/survey/create',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'Survey', id: 'LIST' }],
    }),

    // POST /tenant-survey/create - Specific endpoint for tenant users
    createTenantSurvey: builder.mutation({
      query: (data) => ({
        url: '/survey/create',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [
        { type: 'Survey', id: 'LIST' },
        { type: 'Survey', id: 'TENANT_LIST' }
      ],
    }),

    // PUT /survey/{id} - Admin update
    updateSurvey: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/survey/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Survey', id },
        { type: 'Survey', id: 'LIST' },
      ],
    }),

    // PUT /survey/{id} - Tenant update
    updateTenantSurvey: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/survey/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Survey', id },
        { type: 'Survey', id: 'LIST' },
        { type: 'Survey', id: 'TENANT_LIST' }
      ],
    }),

    // POST /survey/media — temp image upload (returns plain relative path string)
    addSurveyMedia: builder.mutation({
      query: ({ file, orderNo = 0 }) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('orderNo', orderNo.toString());
        return {
          url: '/survey/media',
          method: 'POST',
          body: formData,
          formData: true,
        };
      },
      transformResponse: (response) => response?.value ?? response,
    }),

    // DELETE /survey/{id} - Survey silme
    deleteSurvey: builder.mutation({
      query: (id) => ({
        url: `/survey/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [
        { type: 'Survey', id: 'LIST' },
        { type: 'Survey', id: 'TENANT_LIST' }
      ],
    }),

    // GET /survey/report/{surveyId}
    getSurveyReport: builder.query({
      query: (id) => ({
        url: `/survey/report/${id}`,
        method: 'GET',
      }),
      // Raporun içindeki "value" alanından gerçek rapor dizisini çıkarıyoruz
      transformResponse: (response) => response.value.value,
      providesTags: (result, error, id) => [{ type: 'SurveyReport', id }],
    }),

    // GET /survey/report/{surveyId}/pdf - PDF rapor indirme
    // Backend returns binary PDF as application/octet-stream
    getSurveyPdfReport: builder.query({
      query: (surveyId) => ({
        url: `/survey/report/${surveyId}/pdf`,
        method: 'GET',
        responseHandler: async (response) => {
          // Backend returns binary PDF with content-type: application/octet-stream
          const blob = await response.blob();
          return blob;
        },
      }),
      keepUnusedDataFor: 0,
    }),
  }),
});

export const {
  useGetSurveysQuery,
  useGetSurveysByTenantQuery,
  useGetSurveyByIdQuery,
  useGetTenantSurveyByIdQuery,
  useCreateSurveyMutation,
  useCreateTenantSurveyMutation,
  useUpdateSurveyMutation,
  useUpdateTenantSurveyMutation,
  useDeleteSurveyMutation,
  useAddSurveyMediaMutation,
  useLazyGetSurveyReportQuery,
  useLazyGetSurveyPdfReportQuery,
} = surveyApi;

export const {
  reducer: surveyReducer,
  middleware: surveyMiddleware,
  reducerPath: surveyReducerPath,
} = surveyApi;
